import Vue from '../externals/vue.js'
import type { Toasts } from './toasts.js'

/** Uses the shared Aeppic dialog stack; closing the dialog always means Stay. */
export function showNavigationConfirmation(region: HTMLElement, toasts: Toasts, save: () => Promise<void>, discard: () => void, getAeppicContext: (name: string) => unknown): Promise<boolean> {
  return new Promise(resolve => {
    const host = region.ownerDocument.createElement('div')
    region.appendChild(host)
    let settled = false
    let component

    const finish = (leave: boolean) => {
      if (settled || component.busy) return
      settled = true
      resolve(leave)
      Vue.nextTick(() => {
        component.$destroy()
        host.remove()
      })
    }

    component = new Vue({
      provide: () => ({ getAeppicContext }),
      data: () => ({ busy: false }),
      methods: {
        stay() { finish(false) },
        discard() {
          if (this.busy) return
          discard()
          finish(true)
        },
        async save() {
          if (this.busy) return
          this.busy = true
          const toast = toasts.show('Änderungen werden gespeichert…', { active: true, durationMs: null })
          try {
            await save()
            toasts.complete(toast, { message: 'Änderungen gespeichert.' })
            this.busy = false
            finish(true)
          } catch (error) {
            toasts.complete(toast, { kind: 'error', message: `Speichern fehlgeschlagen: ${error.message || error}` })
            this.busy = false
          }
        },
      },
      render(h) {
        const button = (label: string, action: () => void, primary = false) => h('button', {
          class: { 'ae-button': true, 'ae-button--primary': primary },
          attrs: { type: 'button', disabled: this.busy },
          style: { minHeight: '44px', padding: '10px 16px', borderRadius: '8px', whiteSpace: 'nowrap' },
          on: { click: action },
        }, label)

        return h('ae-dialog', {
          props: {
            open: true,
            title: 'Änderungen speichern?',
            appearance: 'confirmation',
            fullScreen: false,
            adjustDialogSize: false,
            dialogStyle: { width: 'min(680px, calc(100vw - 32px))', minWidth: '0', minHeight: '0' },
            mountRegion: region,
            backdropClose: !this.busy,
          },
          on: { close: this.stay },
          scopedSlots: {
            body: () => h('p', { style: { margin: '0' } }, 'Sie haben ungespeicherte Änderungen. Möchten Sie diese vor dem Verlassen speichern?'),
            commands: () => [
              button('Hier bleiben', this.stay),
              button('Verwerfen', this.discard),
              button(this.busy ? 'Wird gespeichert…' : 'Speichern und weiter', this.save, true),
            ],
          },
        })
      },
    })
    component.$mount()
    host.appendChild(component.$el)
  })
}
